import { ART_BOX, CARD_H, CARD_W } from "./constants.js";
import { median } from "./numeric.js";
import { grayValue, resizeArea, rgbaToLab, toU8 } from "./image.js";

/* ===========================================================================
   Stage 5.3a — the perceptual signature. Ports cardvision/index/hashes.py; every
   resize is INTER_AREA because that is what the index was built through.
   =========================================================================== */

//: Cosine bases per transform size, built once. A scan runs six DCTs per card (two
//: pHashes and the 256-bit art hash, twice for the two orientations) and the basis is
//: a pure function of n — rebuilding it each call was a few thousand cos() per card.
export const DCT_BASES = new Map();

export function dctBasis(n) {
  let cos = DCT_BASES.get(n);
  if (cos) return cos;
  cos = new Float32Array(n * n);
  for (let k = 0; k < n; k++) {
    const alpha = k === 0 ? Math.sqrt(1 / n) : Math.sqrt(2 / n);
    for (let i = 0; i < n; i++)
      cos[k * n + i] = alpha * Math.cos((Math.PI * (2 * i + 1) * k) / (2 * n));
  }
  DCT_BASES.set(n, cos);
  return cos;
}

/** Orthonormal DCT-II, matching cv2.dct's scaling (the DC row's factor differs). */
export function dct2d(values, n) {
  const cos = dctBasis(n);
  const rows = new Float32Array(n * n);
  for (let y = 0; y < n; y++) {
    for (let k = 0; k < n; k++) {
      let acc = 0;
      for (let x = 0; x < n; x++) acc += cos[k * n + x] * values[y * n + x];
      rows[y * n + k] = acc;
    }
  }
  const out = new Float32Array(n * n);
  for (let x = 0; x < n; x++) {
    for (let k = 0; k < n; k++) {
      let acc = 0;
      for (let y = 0; y < n; y++) acc += cos[k * n + y] * rows[y * n + x];
      out[k * n + x] = acc;
    }
  }
  return out;
}

/** Pack 64 booleans MSB-first into [lo, hi] uint32, matching _bits_to_uint64. */
export function bitsToPair(bits) {
  let hi = 0,
    lo = 0;
  for (let i = 0; i < 32; i++) hi = ((hi << 1) | (bits[i] ? 1 : 0)) >>> 0;
  for (let i = 32; i < 64; i++) lo = ((lo << 1) | (bits[i] ? 1 : 0)) >>> 0;
  return [lo, hi];
}

/** Grey plane of an RGBA region, at OpenCV's 8-bit precision. */
export function grayPlane(rgba, w, h, x0, y0, x1, y1) {
  const cw = x1 - x0,
    ch = y1 - y0;
  const out = new Float32Array(cw * ch);
  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) {
      const p = ((y + y0) * w + (x + x0)) * 4;
      out[y * cw + x] = grayValue(rgba[p], rgba[p + 1], rgba[p + 2]);
    }
  }
  return out;
}

export function dhashOf(gray, w, h) {
  // toU8 is what makes this agree with the index: cv2.resize hands dhash uint8, so the
  // comparison is between rounded integers, and two cells that differ by a tenth of a
  // level compare equal there and unequal in float.
  const small = toU8(resizeArea(gray, w, h, 9, 8, 1));
  const bits = new Uint8Array(64);
  for (let y = 0; y < 8; y++)
    for (let x = 0; x < 8; x++) bits[y * 8 + x] = small[y * 9 + x + 1] > small[y * 9 + x] ? 1 : 0;
  return bitsToPair(bits);
}

export function phashOf(gray, w, h) {
  const small = toU8(resizeArea(gray, w, h, 32, 32, 1));
  const coeffs = dct2d(small, 32);
  const low = new Float32Array(64);
  for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) low[y * 8 + x] = coeffs[y * 32 + x];
  const mid = median(low);
  const bits = new Uint8Array(64);
  for (let i = 0; i < 64; i++) bits[i] = low[i] > mid ? 1 : 0;
  return bitsToPair(bits);
}

/** A 256-bit art hash: 16x16 low-frequency block, packed four rows to a word. */
export function phash256Of(gray, w, h) {
  const small = toU8(resizeArea(gray, w, h, 64, 64, 1));
  const coeffs = dct2d(small, 64);
  const low = new Float32Array(256);
  for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) low[y * 16 + x] = coeffs[y * 64 + x];
  const mid = median(low);
  const words = [];
  for (let block = 0; block < 4; block++) {
    const bits = new Uint8Array(64);
    for (let i = 0; i < 64; i++) bits[i] = low[block * 64 + i] > mid ? 1 : 0;
    words.push(bitsToPair(bits));
  }
  return words;
}

/** The whole retrieval signature of a canonical 488 x 680 card. */
export function signatureOf(card) {
  const x0 = Math.round(ART_BOX[0] * CARD_W),
    y0 = Math.round(ART_BOX[1] * CARD_H);
  const x1 = Math.round(ART_BOX[2] * CARD_W),
    y1 = Math.round(ART_BOX[3] * CARD_H);
  const aw = x1 - x0,
    ah = y1 - y0;

  const full = grayPlane(card, CARD_W, CARD_H, 0, 0, CARD_W, CARD_H);
  const art = grayPlane(card, CARD_W, CARD_H, x0, y0, x1, y1);

  // Column order is fixed by IndexWriter.finalize: dhash_art, phash_art, dhash_full, phash_full.
  const hashes = [
    dhashOf(art, aw, ah),
    phashOf(art, aw, ah),
    dhashOf(full, CARD_W, CARD_H),
    phashOf(full, CARD_W, CARD_H),
  ];

  // Colour: Lab a/b means over a 4x4 grid of the art box.
  const artRgba = new Uint8ClampedArray(aw * ah * 4);
  for (let y = 0; y < ah; y++) {
    for (let x = 0; x < aw; x++) {
      const s = ((y + y0) * CARD_W + (x + x0)) * 4,
        d = (y * aw + x) * 4;
      artRgba[d] = card[s];
      artRgba[d + 1] = card[s + 1];
      artRgba[d + 2] = card[s + 2];
      artRgba[d + 3] = 255;
    }
  }
  const artLab = rgbaToLab(artRgba, aw, ah);
  const grid = toU8(resizeArea(artLab, aw, ah, 4, 4, 3));
  const colour = new Uint8Array(32);
  for (let i = 0; i < 16; i++) {
    colour[i] = grid[i * 3 + 1];
    colour[16 + i] = grid[i * 3 + 2];
  }

  // Tiles: 3x3 dHashes over the art box, boundaries from np.linspace(...).astype(int).
  const tiles = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const ty0 = Math.trunc((r * ah) / 3),
        ty1 = Math.trunc(((r + 1) * ah) / 3);
      const tx0 = Math.trunc((c * aw) / 3),
        tx1 = Math.trunc(((c + 1) * aw) / 3);
      const tile = new Float32Array((ty1 - ty0) * (tx1 - tx0));
      for (let y = ty0; y < ty1; y++) {
        for (let x = tx0; x < tx1; x++) tile[(y - ty0) * (tx1 - tx0) + (x - tx0)] = art[y * aw + x];
      }
      tiles.push(dhashOf(tile, tx1 - tx0, ty1 - ty0));
    }
  }
  return { hashes, phash256: phash256Of(art, aw, ah), colour, tiles };
}
